import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { db } from '../api/supabase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faClipboardList, faComments, faCog, faPlus, faHome, faSync, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import './Dashboard.css';

const Dashboard = () => {
    const { auth, userProfile, logout, resetSession, loading: authLoading, sessionValid } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('requests');
    const [userLeads, setUserLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [resetStatus, setResetStatus] = useState('');
    const [sessionError, setSessionError] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            if (authLoading) return;
            
            console.log('Dashboard: Checking auth state', { auth, sessionValid });
            
            if (!auth || !sessionValid) {
                console.log('Dashboard: No valid session, redirecting to login');
                navigate('/login');
                return;
            }
            
            if (!userProfile) {
                console.log('Dashboard: No user profile found, session may be invalid');
                setSessionError(true);
                return;
            }
            
            fetchUserLeads();
        };
        
        checkAuth();
    }, [auth, userProfile, navigate, authLoading, sessionValid]);

    const fetchUserLeads = async () => {
        try {
            setLoading(true);
            setError(null);
            
            if (!userProfile) {
                throw new Error('User profile not found');
            }
            
            const { data, error } = await db.getUserLeads(
                userProfile.id,
                userProfile.user_type
            );
            
            if (error) throw error;
            
            setUserLeads(data || []);
        } catch (err) {
            console.error('Error fetching user leads:', err);
            setError('Failed to load your requests. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const handleResetSession = async () => {
        setResetStatus('Resetting session data...');
        
        try {
            const result = await resetSession();
            setResetStatus(result.message);
            
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err) {
            console.error('Error resetting session:', err);
            setResetStatus('Error resetting session. Please try again.');
        }
    };

    const handleCreateRequest = () => {
        if (userProfile.user_type === 'buyer' || userProfile.user_type === 'both') {
            navigate('/buyers/create_lead');
        } else if (userProfile.user_type === 'seller') {
            navigate('/sellers/create_lead');
        }
    };

    // Format date to a more readable format
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Get status badge class based on status
    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'new':
                return 'status-badge status-new';
            case 'in progress':
                return 'status-badge status-progress';
            case 'completed':
                return 'status-badge status-completed';
            case 'cancelled':
                return 'status-badge status-cancelled';
            default:
                return 'status-badge';
        }
    };

    if (authLoading) {
        return (
            <div className="dashboard-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading your dashboard...</p>
                </div>
            </div>
        );
    }
    
    if (sessionError) {
        return (
            <div className="dashboard-container">
                <div className="session-error">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                    <h2>Session Error</h2>
                    <p>There was a problem with your session. Please reset your session and try again.</p>
                    <button 
                        className="reset-session-button" 
                        onClick={handleResetSession}
                        disabled={resetStatus.includes('Resetting')}
                    >
                        <FontAwesomeIcon icon={faSync} spin={resetStatus.includes('Resetting')} />
                        Reset Session
                    </button>
                    {resetStatus && <p className="info-message">{resetStatus}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            {/* Sidebar */}
            <div className="dashboard-sidebar">
                <div className="sidebar-header">
                    <h2>Appcierge</h2>
                </div>
                
                <div className="sidebar-menu">
                    <button 
                        className={`sidebar-menu-item ${activeTab === 'requests' ? 'active' : ''}`}
                        onClick={() => setActiveTab('requests')}
                    >
                        <FontAwesomeIcon icon={faClipboardList} />
                        <span>My Requests</span>
                    </button>
                    
                    <button 
                        className={`sidebar-menu-item ${activeTab === 'communications' ? 'active' : ''}`}
                        onClick={() => setActiveTab('communications')}
                    >
                        <FontAwesomeIcon icon={faComments} />
                        <span>Communications</span>
                    </button>
                </div>
                
                <div className="sidebar-footer">
                    <button 
                        className={`sidebar-menu-item ${activeTab === 'settings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('settings')}
                    >
                        <FontAwesomeIcon icon={faCog} />
                        <span>Settings</span>
                    </button>
                    
                    <button 
                        className="sidebar-menu-item logout"
                        onClick={handleLogout}
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} />
                        <span>Logout</span>
                    </button>
                </div>
            </div>
            
            {/* Main Content */}
            <div className="dashboard-content">
                <div className="dashboard-header">
                    <h1>
                        {activeTab === 'requests' && 'My Requests'}
                        {activeTab === 'communications' && 'Communications'}
                        {activeTab === 'settings' && 'Settings'}
                    </h1>
                    <div className="user-info">
                        <span>Welcome, {userProfile?.first_name || userProfile?.name || auth?.email}</span>
                    </div>
                </div>
                
                <div className="dashboard-body">
                    {/* My Requests Tab */}
                    {activeTab === 'requests' && (
                        <div className="requests-container">
                            <div className="requests-header">
                                <h2>Your Requests</h2>
                                <button className="create-request-btn" onClick={handleCreateRequest}>
                                    <FontAwesomeIcon icon={faPlus} />
                                    <span>New Request</span>
                                </button>
                            </div>
                            
                            {loading ? (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                    <p>Loading your requests...</p>
                                </div>
                            ) : error ? (
                                <div className="error-container">
                                    <p>{error}</p>
                                    <button onClick={fetchUserLeads}>Try Again</button>
                                </div>
                            ) : userLeads.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-state-icon">
                                        <FontAwesomeIcon icon={faClipboardList} size="3x" />
                                    </div>
                                    <h3>No requests yet</h3>
                                    <p>You haven't submitted any requests yet. Create your first request to get started.</p>
                                    <button className="create-request-btn" onClick={handleCreateRequest}>
                                        <FontAwesomeIcon icon={faPlus} />
                                        <span>Submit a Request</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="requests-list">
                                    {userLeads.map(lead => (
                                        <div className="request-card" key={lead.id}>
                                            <div className="request-header">
                                                <h3>{lead.title || lead.service_type || 'Request'}</h3>
                                                <span className={getStatusBadgeClass(lead.status)}>
                                                    {lead.status || 'New'}
                                                </span>
                                            </div>
                                            <div className="request-details">
                                                <p>{lead.description || 'No description provided'}</p>
                                                <div className="request-meta">
                                                    <span>Submitted: {formatDate(lead.created_at)}</span>
                                                    {lead.updated_at && lead.updated_at !== lead.created_at && (
                                                        <span>Updated: {formatDate(lead.updated_at)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Communications Tab */}
                    {activeTab === 'communications' && (
                        <div className="communications-container">
                            <div className="communications-sidebar">
                                <div className="communications-search">
                                    <input type="text" placeholder="Search conversations..." />
                                </div>
                                <div className="conversations-list">
                                    <div className="empty-conversations">
                                        <p>No active conversations</p>
                                    </div>
                                </div>
                            </div>
                            <div className="communications-main">
                                <div className="empty-state">
                                    <div className="empty-state-icon">
                                        <FontAwesomeIcon icon={faComments} size="3x" />
                                    </div>
                                    <h3>No conversation selected</h3>
                                    <p>Select a conversation from the list or start a new one.</p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Settings Tab */}
                    {activeTab === 'settings' && (
                        <div className="settings-container">
                            <div className="settings-section">
                                <h2>Profile Settings</h2>
                                <p>Manage your profile information and account settings.</p>
                                <button 
                                    className="settings-btn" 
                                    onClick={() => navigate('/profile')}
                                >
                                    Edit Profile
                                </button>
                            </div>
                            
                            <div className="settings-section">
                                <h2>Account Type</h2>
                                <p>Your account type: <strong>{userProfile?.user_type || 'User'}</strong></p>
                            </div>
                            
                            <div className="settings-section">
                                <h2>Email Notifications</h2>
                                <div className="settings-option">
                                    <label>
                                        <input type="checkbox" defaultChecked />
                                        Receive notifications for new messages
                                    </label>
                                </div>
                                <div className="settings-option">
                                    <label>
                                        <input type="checkbox" defaultChecked />
                                        Receive notifications for request updates
                                    </label>
                                </div>
                            </div>

                            <div className="settings-section">
                                <h2>Session Management</h2>
                                <p>If you're experiencing login or logout issues, you can reset your session data.</p>
                                <button 
                                    className="settings-btn reset-btn" 
                                    onClick={handleResetSession}
                                    disabled={resetStatus.includes('Resetting')}
                                >
                                    <FontAwesomeIcon icon={faSync} spin={resetStatus.includes('Resetting')} />
                                    Reset Session
                                </button>
                                {resetStatus && <p className="reset-status">{resetStatus}</p>}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard; 